import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import { useGetAppExclusiveSlotCheck } from '~/hooks/apis/offers/useGetAppExclusiveSlotCheck';
import { useMoveAppExclusiveSchedule } from '~/hooks/apis/offers/useMoveAppExclusiveSchedule';

import type { ScheduleRecord } from '~/services/OffersService';

import {
  formatDayLabel,
  formatSlotTime,
  getScheduleDate,
  getScheduleStatus,
  getSlotBlockReason,
  todayDate,
} from './appOnlyFlashCalendar';

const DATE_FORMAT = 'YYYY-MM-DD';

interface Props {
  /** Row being moved; null while the dialog is closed. */
  schedule: ScheduleRecord | null;
  regionName: string;
  getOfferName: (offerId: string) => string | undefined;
  onClose: () => void;
}

export default function MoveAppOnlyFlashDealDialog({ schedule, regionName, getOfferName, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const open = schedule !== null;
  const fromDate = schedule ? getScheduleDate(schedule) : todayDate();
  // Derived on the same render the dialog opens, so the first slot check is for the default day.
  // Setting it in an effect fired one check for a stale day first, which was then aborted.
  const defaultDate = dayjs.utc(fromDate, DATE_FORMAT).add(1, 'day').format(DATE_FORMAT);
  // Tagged with the row it was picked for, so reopening on another deal starts from its default.
  const [picked, setPicked] = useState<{ scheduleId: number; date: string } | null>(null);
  const date = schedule?.id !== undefined && picked?.scheduleId === schedule.id ? picked.date : defaultDate;

  const offerId = schedule?.fk_offer_salesforce_id ?? '';
  const region = schedule?.region ?? '';
  const offerName = getOfferName(offerId) ?? offerId;
  const isSameDay = date === fromDate;

  const targetCheck = useGetAppExclusiveSlotCheck(
    { region, date, offerId, offerName: getOfferName(offerId) },
    open && !isSameDay,
  );
  const eligible = targetCheck.data;

  // The deal on the target day, when it hasn't started and could take this deal's old day instead.
  const taken = eligible?.dayTakenBy && eligible.dayTakenBy.scheduleId !== schedule?.id ? eligible.dayTakenBy : null;
  const other: ScheduleRecord | null =
    taken && schedule && getScheduleStatus(taken) === 'scheduled'
      ? {
          ...schedule,
          id: taken.scheduleId,
          fk_offer_salesforce_id: taken.offerId,
          start: taken.start,
          end: taken.end,
        }
      : null;
  const otherName = other ? (getOfferName(other.fk_offer_salesforce_id) ?? other.fk_offer_salesforce_id) : '';

  const swapCheck = useGetAppExclusiveSlotCheck(
    {
      region,
      date: fromDate,
      offerId: other?.fk_offer_salesforce_id ?? '',
      offerName: other ? getOfferName(other.fk_offer_salesforce_id) : undefined,
    },
    open && !!other,
  );

  const ignore = [schedule?.id, other?.id].filter((id): id is number => id !== undefined);
  const moveReason = eligible && schedule ? getSlotBlockReason(eligible, offerId, ignore) : undefined;
  const swapReason =
    other && swapCheck.data ? getSlotBlockReason(swapCheck.data, other.fk_offer_salesforce_id, ignore) : undefined;
  const isChecking = targetCheck.isFetching || (!!other && swapCheck.isFetching);
  const canSubmit =
    !!schedule &&
    !!eligible &&
    !isSameDay &&
    !isChecking &&
    !moveReason &&
    (!other || (!!swapCheck.data && !swapReason));

  const moveSchedule = useMoveAppExclusiveSchedule();

  const handleSubmit = async () => {
    if (!schedule || !eligible || !canSubmit) {
      return;
    }
    try {
      await moveSchedule.mutateAsync({
        schedule,
        searchName: getOfferName(offerId) ?? null,
        targetDate: date,
        swapWith: other
          ? {
              schedule: other,
              offerName: otherName,
              searchName: getOfferName(other.fk_offer_salesforce_id) ?? null,
              targetDate: fromDate,
            }
          : undefined,
      });
      enqueueSnackbar(
        other
          ? `Swapped ${offerName} (now ${formatDayLabel(date)}) with ${otherName} (now ${formatDayLabel(fromDate)})`
          : `${offerName} moved to ${formatDayLabel(date)}`,
        { variant: 'success' },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(`Could not move deal: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="move-app-only-flash-deal-title">
      <DialogTitle id="move-app-only-flash-deal-title">Move deal · {regionName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            <strong>{offerName}</strong> is on {formatDayLabel(fromDate)}. Pick the day it should run instead. If
            another upcoming deal holds that day, the two swap.
          </Typography>
          <DatePicker
            label="New day"
            format="ddd D MMM YYYY"
            value={dayjs(date, DATE_FORMAT)}
            minDate={dayjs(todayDate(), DATE_FORMAT)}
            onChange={(value) =>
              schedule && value?.isValid() && setPicked({ scheduleId: schedule.id, date: value.format(DATE_FORMAT) })
            }
            slotProps={{ textField: { required: true } }}
          />

          {isSameDay && <Alert severity="info">That's the day it already runs on.</Alert>}
          {isChecking && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Checking the day...
              </Typography>
            </Stack>
          )}
          {targetCheck.isError && <Alert severity="error">Could not check the day: {targetCheck.error?.message}</Alert>}
          {!isChecking && eligible && !isSameDay && (
            <>
              <Typography variant="body2" color="text.secondary">
                Slot runs {formatSlotTime(eligible.slot.start)} to {formatSlotTime(eligible.slot.end)} (
                {APP_EXCLUSIVE_TIMEZONE}).
              </Typography>
              {moveReason && (
                <Alert severity="warning">
                  {offerName} can't run on {formatDayLabel(date)}: {moveReason}.
                </Alert>
              )}
              {!moveReason && other && swapReason && (
                <Alert severity="warning">
                  Can't swap: {otherName} can't run on {formatDayLabel(fromDate)}: {swapReason}.
                </Alert>
              )}
              {!moveReason && other && !swapReason && swapCheck.data && (
                <Alert severity="info">
                  {formatDayLabel(date)} has {otherName}. It will move to {formatDayLabel(fromDate)}.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit || moveSchedule.isPending}>
          {moveSchedule.isPending ? 'Moving...' : other ? 'Swap deals' : 'Move deal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
